import * as React from "react";
import { StatusBar, StyleSheet, View } from "react-native";
import * as Font from "expo-font";
import { NavigationContainer } from "@react-navigation/native";
import { TransitionPresets } from "@react-navigation/stack";
import GlobalSettingsProvider, {
  GlobalSettingsContext,
} from "./context/GlobalSettingsProvider.js";
import AvailableUserSettingsProvider, {
  AvailableUserSettingsContext,
} from "./context/AvailableUserSettingsProvider.js";
import { createStackNavigator } from "@react-navigation/stack";
import BottomTabNavigator from "./navigation/BottomTabNavigator";
import ArticleScreen from "./screens/ArticleScreen.js";
import NavbarMobileScreen from "./screens/NavbarMobileScreen.js";
import InterstitialAdvertScreen from "./screens/InterstitialAdvertScreen.js";
import * as SecureStore from "expo-secure-store";
import AsyncStorage from "@react-native-async-storage/async-storage";
import TabBarIcon from "./components/TabBarIcon";
import { backendUrl } from "./constants/Environment.js";
import { AuthProvider } from "./context/AuthenticationProvider.js";
import { provider, ProviderComposer } from "./context/providers.js";
import AppDataProvider, { AppDataContext } from "./context/AppDataProvider.js";

const Stack = createStackNavigator();

export default function AppWrapper() {
  return (
    <ProviderComposer
      providers={[
        provider(AppDataProvider),
        provider(GlobalSettingsProvider),
        provider(AvailableUserSettingsProvider),
      ]}
    >
      <App />
    </ProviderComposer>
  );
}

function App(props) {
  const [isLoadingComplete, setLoadingComplete] = React.useState(false);
  const [initialNavigationState, setInitialNavigationState] = React.useState();
  const containerRef = React.useRef();

  const {
    setCategoriesList,
    isLoggedIn,
    setLoggedIn,
    isDarkMode,
    colors,
    setForceLogin,
  } = React.useContext(AppDataContext);

  const [myArticleId, setMyArticleId] = React.useState(-1);

  const [myUsername, setMyUsername] = React.useState("");
  const [myAuthToken, setMyAuthToken] = React.useState("");

  const [myUserId, setMyUserId] = React.useState(-1);
  const [myUserSettings, setMyUserSettings] = React.useState([]);

  const [myUserInfo, setMyUserInfo] = React.useState([]);

  const [showBannerAds, setShowBannerAds] = React.useState(false);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    containerInside: {
      flex: 12,
    },
    bottomView: {
      flex: 1,
    },
  });

  /**
   * The name of the cookies in the browser.
   */
  const authName = "authentication_data";

  /**
   * For managing global settings like [app version]
   */
  const { saveGlobalSettingsMany } = React.useContext(GlobalSettingsContext);

  /**
   * For managing available user settings like [app dark mode]
   */
  const { saveAvailableSettingsMany } = React.useContext(
    AvailableUserSettingsContext
  );

  React.useEffect(() => {
    async function loadResourcesAndDataAsync() {
      try {
        /**
         * Load our fonts
         */
        await Font.loadAsync({
          "montserrat-light": require("./assets/fonts/Montserrat-Light.ttf"),
        });
        await Font.loadAsync({
          "Montserrat-Light": require("./assets/fonts/Montserrat-Light.ttf"),
        });
        await Font.loadAsync({
          MontserratLight: require("./assets/fonts/Montserrat-Light.ttf"),
        });
        
        await Font.loadAsync({
          "oswald-regular": require("./assets/fonts/Oswald-Regular.ttf"),
        });
      } catch (e) {
        console.warn(e);
      } finally {
      }
    }

    /**
     * For managing user authentication via tokens
     */
    async function setAuthTokenData() {
      /**
       * There are two main functionalities here, one for logging in via a real mobile device,
       * and one for the expo emulator/web. The emulator version is useful for development purposes.
       */
      var isAllowed = await SecureStore.isAvailableAsync();
      if (!isAllowed) {
        const authData = await AsyncStorage.getItem(authName);
        if (authData) {
          /**
           * We've found some auth data, let's send it to our back-end and verify!
           */
          if (authData !== null) {
            const authDataJson = JSON.parse(authData);

            /**
             * Read the data from backend
             */
            fetch(`${backendUrl}/app/verify_authentication`, {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + authDataJson.token,
              },
              body: JSON.stringify({
                authToken: authDataJson.token,
                username: authDataJson.username,
                deviceId: authDataJson.deviceId,
              }),
              method: "POST",
            })
              .then((res) => res.json())
              .then((data) => {
                if (data) {
                  /**
                   * User is officially logged in!
                   */
                  setMyUserId(data.id);
                  setMyUsername(authDataJson.username);
                  setMyAuthToken(authDataJson.token);

                  /**
                   * Gather the user's settings and then we can start!
                   */
                  fetch(backendUrl + "/settings/user/" + data.id, {
                    headers: {
                      Accept: "application/json",
                      "Content-Type": "application/json",
                      Authorization: "Bearer " + authDataJson.token,
                    },
                    method: "GET",
                  })
                    .then((res) => res.json())
                    .then((data) => {
                      setMyUserSettings(data);

                      var dataSideColour = data.find(
                        (az) =>
                          az.id > 0 &&
                          az.setting &&
                          az.setting.description === "App Dark Mode"
                      );

                      if (dataSideColour && dataSideColour.rawValue) {
                        setScheme(dataSideColour.rawValue);
                      }

                      setLoggedIn(true);

                      /**
                       * Load user info, such as [bought-premium] etc?
                       */
                      fetch(`${backendUrl}/users/id/${authDataJson.id}`, {
                        headers: {
                          Accept: "application/json",
                          "Content-Type": "application/json",
                          Authorization: "Bearer " + authDataJson.token,
                        },
                        method: "GET",
                      })
                        .then((res) => res.json())
                        .then((data) => {
                          setMyUserInfo(data[0]);
                        });
                    });
                }
              })
              .catch((e) => {
                console.log(e);
                setLoggedIn(false);
              });
          } else {
            setLoggedIn(false);
          }
        } else {
          setLoadingComplete(true);
          setLoggedIn(false);
        }
      } else {
        try {
          const credentials = await SecureStore.getItemAsync(authName);

          if (credentials) {
            const authDataJson = JSON.parse(credentials);

            let credToken = "";

            if (authDataJson.token && authDataJson.token.length > 0) {
              credToken = authDataJson.token;
            } else if (
              authDataJson.authToken &&
              authDataJson.authToken.length > 0
            ) {
              credToken = authDataJson.authToken;
            }

            /**
             * Read the data from backend
             */
            fetch(`${backendUrl}/app/verify_authentication`, {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + credToken,
              },
              body: JSON.stringify({
                authToken: credToken,
                username: authDataJson.username,
                deviceId: authDataJson.deviceId,
              }),
              method: "POST",
            })
              .then((response) => response.json())
              .then((data) => {
                if (data) {
                  /**
                   * User is officially logged in!
                   */
                  setMyUserId(data.id);
                  setMyUsername(authDataJson.username);
                  setMyAuthToken(credToken);
                  setLoggedIn(true);

                  /**
                   * Gather the user's settings and then we can start!
                   */
                  fetch(backendUrl + "/settings/user/" + data.id, {
                    headers: {
                      Accept: "application/json",
                      "Content-Type": "application/json",
                      Authorization: "Bearer " + credToken,
                    },
                    method: "GET",
                  })
                    .then((res) => res.json())
                    .then((dataSettings) => {
                      if (!dataSettings) {
                        return;
                      }
                      setMyUserSettings(dataSettings);
                      var dataSideColour = dataSettings.find(
                        (az) =>
                          az.id > 0 &&
                          az.setting &&
                          az.setting.description === "App Dark Mode"
                      );

                      if (dataSideColour) {
                        setScheme(dataSideColour.rawValue);
                      }

                      setLoggedIn(true);

                      let userInfoUrl =
                        backendUrl + "/users/id/" + authDataJson.id;

                      /**
                       * Load user info, such as [bought-premium] etc?
                       */
                      fetch(userInfoUrl, {
                        headers: {
                          Accept: "application/json",
                          "Content-Type": "application/json",
                          Authorization: "Bearer " + credToken,
                        },
                        method: "GET",
                      })
                        .then((res) => res.json())
                        .then((dataUserInfo) => {
                          setMyUserInfo(dataUserInfo[0]);
                        });
                    });
                }
              })
              .catch((e) => {
                console.log(e);
                setLoadingComplete(false);
                setLoggedIn(false);
              });

            setLoadingComplete(true);
            setLoggedIn(true);
          } else {
            setLoggedIn(false);
          }
        } catch (e) {
          console.log(e);
        }
      }
    }

    async function loadSettings() {
      fetch(`${backendUrl}/globalsettings/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
      })
        .then((res) => res.json())
        .then((data) => {
          var arrayToAppend = [];
          data.forEach((a, b) => {
            var entrance = {
              id: a.id,
              settingId: a.settingId,
              description: a.available_setting
                ? a.available_setting.description
                : "Unknown",
              value: a.unconstrainedValue,
            };

            /**
             * This is if we're actually providing banner ads, not if the user is showing/hiding them.
             */
            if (entrance.description === "Show Banner Ads") {
              if (entrance.value === "True") {
                setShowBannerAds(true);
              } else {
                setShowBannerAds(false);
              }
            }

            if (entrance.description === "Allow unregistered access") {
              if (entrance.value === "True") {
                setForceLogin(false);
              } else {
                setForceLogin(true);
              }
            }

            /**
             * Fill up the global settings provider so we can access the data later.
             */
            arrayToAppend.push(entrance);
          });
          setLoadingComplete(true);
          saveGlobalSettingsMany(arrayToAppend);
        })
        .catch((e) => {
          console.log(e);
          setLoadingComplete(true);
        });
    }

    async function loadAvailableSettings() {
      if (!myAuthToken) {
        return;
      }
      fetch(backendUrl + "/settings/", {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + myAuthToken,
        },
        method: "GET",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data) {
            var arrayToAppend = [];
            data.forEach((a, b) => {
              var entrance = {
                id: a.id,
                description: a.description ? a.description : "Unknown",
              };

              /**
               * Fill up the global settings provider so we can access the data later.
               */
              arrayToAppend.push(entrance);
            });
            setLoadingComplete(true);
            saveAvailableSettingsMany(arrayToAppend);
          }
        })
        .catch((e) => {
          console.log(e);
          setLoadingComplete(true);
        });
    }

    /**
     * Load categories for the app data provider
     */
    fetch(`${backendUrl}/categories/`, {
      method: "GET",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
        Accept: "application/json",
        Authorization: "Bearer " + myAuthToken,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        setCategoriesList(data);
      });

    loadResourcesAndDataAsync();
    setAuthTokenData();
    loadSettings();

    /**
     * Useful for settings that haven't been set yet, e.g. if a new user joins and they
     * don't have anything for [App Dark Mode], the app will know such option exists
     * in the system.
     */
    loadAvailableSettings();

    setLoadingComplete(true);
  }, [showBannerAds, myUserId, myAuthToken, isLoggedIn]);

  function setLoggedInSuccessfully() {
    setLoggedIn(true);
  }

  async function handleLogout() {
    try {
      var isMobile = await SecureStore.isAvailableAsync();
      if (!isMobile) {
        AsyncStorage.removeItem("authentication_data");
      } else {
        SecureStore.deleteItemAsync("authentication_data");
      }
    } catch (exception) {
      console.log(exception);
    }
    setLoggedIn(false);
  }

  function setCategory(id) {
    setMyArticleId(id);
  }

  const config = {
    animation: "spring",
    config: {
      stiffness: 1000,
      damping: 500,
      mass: 3,
      overshootClamping: true,
      restDisplacementThreshold: 0.01,
      restSpeedThreshold: 0.01,
    },
  };

  if (!isLoadingComplete) {
    return <></>;
  } else {
    return (
      <AuthProvider>
        <View style={styles.container}>
          <View style={styles.containerInside}>
            <StatusBar hidden />
            <NavigationContainer
              theme={{
                dark: isDarkMode,
                colors: {
                  backgroundColor: colors.background,
                  primary: colors.primary,
                  background: colors.background,
                  text: colors.text,
                },
              }}
              ref={containerRef}
              initialState={initialNavigationState}
            >
              <Stack.Navigator>
                <Stack.Group>
                  <Stack.Screen
                    name="Root"
                    options={{
                      headerShown: false,
                      tabBarVisible: false,
                    }}
                  >
                    {(props) => (
                      <BottomTabNavigator
                        {...props}
                        setLoggedIn={setLoggedInSuccessfully}
                        isLoggedIn={isLoggedIn}
                        setCategory={setCategory}
                        articleId={myArticleId}
                        username={myUsername}
                        userId={myUserId}
                        myUserSettings={myUserSettings}
                        authToken={myAuthToken}
                        setAuthToken={setMyAuthToken}
                        handleLogout={handleLogout}
                        userDetails={myUserInfo}
                      />
                    )}
                  </Stack.Screen>
                  <Stack.Screen
                    name="ChooseArticle"
                    initialParams={{ id: 1 }}
                    options={{
                      title: "ChooseArticle",
                      headerShown: false,
                      tabBarVisible: false,
                      transitionSpec: {
                        open: config,
                        close: config,
                      },
                      tabBarIcon: ({ focused }) => (
                        <TabBarIcon focused={focused} name="md-flutter-dash" />
                      ),
                    }}
                  >
                    {(props) => (
                      <ArticleScreen
                        {...props}
                        authToken={myAuthToken}
                        username={myUsername}
                        articleId={myArticleId}
                        userId={myUserId}
                        userDetails={myUserInfo}
                      />
                    )}
                  </Stack.Screen>
                  <Stack.Screen
                    name="InterstitialAdvertScreen"
                    options={{
                      title: "InterstitialAdvertScreen",
                      headerShown: false,
                      tabBarVisible: false,
                      tabBarIcon: ({ focused }) => (
                        <TabBarIcon focused={focused} name="md-settings" />
                      ),
                    }}
                  >
                    {(props) => <InterstitialAdvertScreen {...props} />}
                  </Stack.Screen>
                </Stack.Group>
                <Stack.Group
                  screenOptions={{
                    presentation: "modal",
                    headerShown: false,
                  }}
                >
                  <Stack.Screen
                    name="MyModal"
                    component={NavbarMobileScreen}
                    options={{
                      title: "Modal",
                      ...TransitionPresets.ModalSlideFromBottomIOS,
                    }}
                  />
                </Stack.Group>
              </Stack.Navigator>
            </NavigationContainer>
          </View>
        </View>
      </AuthProvider>
    );
  }
}
